import type { AvatarData } from './generated/avatar-runtime';
import type { AnimationName as ExportedAnimationName } from './generated/strobi.avatar';
import { withContextAnimations } from './contextAnimations';
import type { MascotAnimationName } from './contextAnimations';
import { MASCOT_IDS, MascotId } from './types';

type MascotModule = Readonly<{
  avatarData: AvatarData<ExportedAnimationName>;
}>;
type MascotLoader = () => Promise<MascotModule>;
type NativePalette = Readonly<{ body: string; eyes: string }>;

export type MascotCatalogEntry = Readonly<{
  id: MascotId;
  name: string;
  nativeColors?: NativePalette;
}>;

export const MASCOT_CATALOG: Record<MascotId, MascotCatalogEntry> = {
  strobi: { id: 'strobi', name: 'Strobi' },
  freddy: { id: 'freddy', name: 'Freddy' },
  citrus: { id: 'citrus', name: 'Citrus' },
  nova: { id: 'nova', name: 'Nova' },
  'grok-bot': { id: 'grok-bot', name: 'Grok bot' },
  kirby: { id: 'kirby', name: 'Kirby' },
  cloudee: { id: 'cloudee', name: 'Cloudee' },
  cubee: { id: 'cubee', name: 'Cubee' },
  onee: { id: 'onee', name: 'Onee' },
  totto: { id: 'totto', name: 'Totto' },
  cometa: { id: 'cometa', name: 'Cometa' },
  nibbli: { id: 'nibbli', name: 'Nibbli' },
  cresto: { id: 'cresto', name: 'Cresto' },
  duo: { id: 'duo', name: 'Duo' },
  penta: { id: 'penta', name: 'Penta' },
  jelli: { id: 'jelli', name: 'Jelli' },
  prisma: { id: 'prisma', name: 'Prisma' },
  marine: {
    id: 'marine',
    name: 'Marine',
    nativeColors: { body: '#c7bdb2', eyes: '#313841' },
  },
  bouguasse: {
    id: 'bouguasse',
    name: 'Bougasse',
    nativeColors: { body: '#f9ca61', eyes: '#24282a' },
  },
  quentin: {
    id: 'quentin',
    name: 'Quentin',
    nativeColors: { body: '#ecb45c', eyes: '#34393d' },
  },
  yve: {
    id: 'yve',
    name: 'Yves',
    nativeColors: { body: '#9da8ac', eyes: '#293033' },
  },
  karine: {
    id: 'karine',
    name: 'Karine',
    nativeColors: { body: '#c95b36', eyes: '#241f1d' },
  },
  stef: {
    id: 'stef',
    name: 'Stef',
    nativeColors: { body: '#2f8f78', eyes: '#171b19' },
  },
  estelle: {
    id: 'estelle',
    name: 'Estelle',
    nativeColors: { body: '#7b452b', eyes: '#181614' },
  },
};

const mascotLoaders: Record<MascotId, MascotLoader> = {
  strobi: () => import('./generated/strobi.avatar'),
  freddy: () => import('./generated/freddy.avatar'),
  citrus: () => import('./generated/citrus.avatar'),
  nova: () => import('./generated/nova.avatar'),
  'grok-bot': () => import('./generated/grok-bot.avatar'),
  kirby: () => import('./generated/kirby.avatar'),
  cloudee: () => import('./generated/cloudee.avatar'),
  cubee: () => import('./generated/cubee.avatar'),
  onee: () => import('./generated/onee.avatar'),
  totto: () => import('./generated/totto.avatar'),
  cometa: () => import('./generated/cometa.avatar'),
  nibbli: () => import('./generated/nibbli.avatar'),
  cresto: () => import('./generated/cresto.avatar'),
  duo: () => import('./generated/duo.avatar'),
  penta: () => import('./generated/penta.avatar'),
  jelli: () => import('./generated/jelli.avatar'),
  prisma: () => import('./generated/prisma.avatar'),
  marine: () => import('./generated/marine.avatar'),
  bouguasse: () => import('./generated/bouguasse.avatar'),
  quentin: () => import('./generated/quentin.avatar'),
  // The source filenames predate the final Yves/Karine naming correction.
  yve: () => import('./generated/karine.avatar'),
  karine: () => import('./generated/yve.avatar'),
  stef: () => import('./generated/stef.avatar'),
  estelle: () => import('./generated/estelle.avatar'),
};

const mascotData = new Map<
  MascotId,
  Promise<AvatarData<MascotAnimationName>>
>();

export function loadMascotData(
  mascotId: MascotId,
): Promise<AvatarData<MascotAnimationName>> {
  const cached = mascotData.get(mascotId);
  if (cached) return cached;
  const request = mascotLoaders[mascotId]().then(({ avatarData }) => (
    withContextAnimations(avatarData)
  ));
  mascotData.set(mascotId, request);
  return request;
}

export const EXPORTED_MASCOT_ANIMATIONS = Object.freeze([
  'idle',
  'listening',
  'thinking',
  'excited',
  'bored',
  'suspicious',
  'angry',
  'happy',
  'curious',
  'confused',
  'surprised',
  'proud',
  'shy',
  'sad',
  'laughing',
  'scared',
  'playful',
  'celebrate',
  'losing-angry',
  'last-place',
] as const satisfies readonly MascotAnimationName[]);

export function isMascotId(value: unknown): value is MascotId {
  return typeof value === 'string' && (MASCOT_IDS as readonly string[]).includes(value);
}

export function resolveMascotAnimation(value: string | null | undefined): MascotAnimationName {
  return value && (EXPORTED_MASCOT_ANIMATIONS as readonly string[]).includes(value)
    ? (value as MascotAnimationName)
    : 'idle';
}
